// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardDesigner.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { propTypes as ObservablePropTypes } from 'mobx-react';
import { observe } from 'mobx';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import DashboardDesignerToolbar from './Izenda.DashboardDesignerToolbar';
import DashboardDesignerHeader from './Izenda.DashboardDesignerHeader';
import DashboardDesignerBody from './Izenda.DashboardDesignerBody';
import DashboardPresetPopup from './DashboardPresetPopup/Izenda.DashboardPresetPopup';
import Alerts from 'modules/ReportDesigner/Toolbar/Izenda.ReportDesignerAlerts';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import RouteUtils from 'share/utils/Izenda.RouteUtils';
import DashboardConstants from 'logic/data/Izenda.DashboardConstants';
import SecurityManagerStore from 'logic/Izenda.SecurityManagerStore';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import { isNil, get, isEmpty, getOr } from 'lodash/fp';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import { ALERT_TYPES, SHOW_TYPES } from 'logic/data/Izenda.AlertConstants';

const FIRST_INDEX = 0;

const DashboardAlert = connect()(function DashboardAlert(props: any) {
  const {
    alertDetails: { message, alertType, key: alertKey, success, errors, showType, type }
  } = props;

  return (
    <div className='izenda-Alert-container'>
      <Alerts
        message={message}
        alertType={alertType}
        alertKey={alertKey}
        success={success}
        errors={errors}
        type={type}
        showType={showType}
      />
    </div>
  );
});

@connect()
class BackgroundLayer extends Component {
  constructor(props) {
    super(props);
    props.store.validateBackgroundImage();
  }

  componentWillUpdate() {
    this.props.store.validateBackgroundImage();
  }

  render() {
    const {
      store: { dashboardDetail, dashboardDesignerMode }
    } = this.props;

    const dashboard = dashboardDetail.dashboard;
    const style = {
      backgroundImage: dashboardDesignerMode.isDesigner && `url(${dashboard.imageUrl})`,
      backgroundColor: dashboardDesignerMode.isDesigner && dashboard.backgroundColor
    };
    const classes = classnames('izenda-Wrapper-content izenda-DashboardDesigner-LeftContent', {
      'stretch-background-image': dashboard.stretchImage && dashboard.imageUrl,
      'center-background-image': !dashboard.stretchImage && dashboard.imageUrl
    });

    return (
      <div className={classes} style={style}>
        {this.props.children}
      </div>
    );
  }
}

@connect()
export default class DashboardDesigner extends Component {
  static propTypes = {
    store: ObservablePropTypes.observableObject.isRequired,
    newDashboard: PropTypes.bool.isRequired
  };

  constructor() {
    super();
    this.securityManagerStore = new SecurityManagerStore();
    this.startTime = new Date().getTime();
  }

  handleNeedUpdateResultChanged = (change: boolean) => {
    const {
      localization,
      store: {
        dashboardDesignerMode: { alertDetails }
      }
    } = this.props;

    if (change) {
      alertDetails.set({
        message: localization.getText('REPORTDESIGNER_UPDATE_RESULT'),
        type: ALERT_TYPES.INFO,
        success: true,
        showType: SHOW_TYPES.ALWAYS
      });
    } else {
      alertDetails.reset();
    }
  };

  componentDidMount() {
    const {
      securityManagerStore,
      props,
      props: {
        authorization,
        openPopup,
        location: { query },
        store,
        store: { dashboardDetail },
        newDashboard
      }
    } = this;
    const id = props.id || props.params.id;
    const commonFilters = RouteUtils.transformQueryToFilters(query);
    const hasAddNewPrivileges = authorization.checkPrivileges([
      AuthorizationConstants.PERMISSION_KEY.DashboardsCanCreateNewDashboardValue
    ]);

    if (!isNil(id)) {
      securityManagerStore
        .checkAccessPage(props, id, accessPriority => accessPriority !== ACCESS_RIGHT.NoAccess)
        .then(canAccessPage => {
          canAccessPage && this.loadOpeningDashboard(id, commonFilters, store);
        });
    }

    if (newDashboard && hasAddNewPrivileges) {
      openPopup(DashboardPresetPopup, {
        dashboardDetail
      });
    }

    this.observeNeedUpdateResult();
  }

  /**
   * Observe new need update result when new dashboard filter is created
   */
  observeNeedUpdateResult() {
    const {
      store: { dashboardFilterStore }
    } = this.props;

    // Remove old observe
    this.needUpdateResultChangedDisposer && this.needUpdateResultChangedDisposer();

    this.needUpdateResultChangedDisposer = observe(
      dashboardFilterStore.reportFilter,
      'needUpdateResult',
      this.handleNeedUpdateResultChanged
    );
  };

  componentDidUpdate() {
    this.observeNeedUpdateResult();
  }

  componentWillReceiveProps(nextProps) {
    nextProps.store.startDashboardDataWorker(FIRST_INDEX);
  }

  componentWillUnmount() {
    this.props.store.terminateAllWorker();
    this.needUpdateResultChangedDisposer && this.needUpdateResultChangedDisposer();
  }

  render() {
    const {
      store,
      store: { dashboardDesignerMode, dashboardDetail }
    } = this.props;

    const classes = classnames('izenda-Wrapper-content u_paddingTopBottom u_marginLeftRight', {
      'u-bgDarkGray': dashboardDesignerMode.isPresentation
    });

    return (
      <div className='izenda-DashboardDesigner izenda-Wrapper'>
        <div className='izenda-Wrapper-header'>
          <DashboardAlert alertDetails={store.dashboardDesignerMode.alertDetails} />
          <DashboardDesignerToolbar {...this.props} />
        </div>
        <BackgroundLayer {...this.props}>
          <ScrollBox>
            <div className='u_paddingLeftRight u_fullHeight'>
              <DashboardDesignerHeader {...this.props} dashboardDetail={dashboardDetail} />

              <div className={classes}>
                <DashboardDesignerBody {...this.props} dashboardDetail={dashboardDetail} />
              </div>
            </div>
          </ScrollBox>
        </BackgroundLayer>
      </div>
    );
  }

  /**
   * Load all data for dashboard when opening
   */
  loadOpeningDashboard = (id: string, commonFilters: Array<any>, store: any): void => {
    const dashboardDesignerMode = get('model.dashboardDesignerMode', store);
    const {
      props,
      props: { location }
    } = this;

    const pageId = getOr(null, 'state.page', location);

    dashboardDesignerMode.reset();

    // Get data for edit case
    return store
      .loadDashboard(id, commonFilters, pageId)
      .then(dashboardDesignerMode.setEditMode.bind(dashboardDesignerMode))
      .then(store.validateDashboard.bind(store))
      .then(result => {
        if (!result.success) {
          if (result.messages[0].detail) {
            return props.confirmFn('Information', {
              description: DashboardConstants.validateFilterMessage(result),
              type: 'error',
              isHTMLFormat: true,
              showCloseIndicator: true
            });
          } else {
            return props.validation.updateErrors(result.messages);
          }
        }
        // reload dashboard when common filter values came from URI.
        if (!isNil(commonFilters) && !isEmpty(commonFilters)) {
          store.updateResults(true);
        }
        return null;
      })
      .then(() => {
        const renderingTime = new Date().getTime() - this.startTime;
        return store.updateRenderingTime(renderingTime);
      });
  };
}



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/Izenda.DashboardDesigner.js